import type { Probe } from '../services/data';

export type ProbeStatus = 'ok' | 'warning' | 'alarm' | 'offline' | 'unknown';

// Firmware status bitfield (firmware_status in measurements)
const FW_ALARM_HIGH     = 0x01;
const FW_ALARM_LOW      = 0x02;
const FW_WARNING_HIGH   = 0x04;
const FW_WARNING_LOW    = 0x08;
const FW_UNCALIBRATED   = 0x10;
const FW_CERT_EXPIRED   = 0x20;

// Ack bitfield (ack_flags in probes table)
const ACK_UNCALIBRATED  = 0x01;
const ACK_CERT_EXPIRED  = 0x02;

export function calculateProbeStatus(
  currentValue: number | undefined,
  probe: Probe,
  hasRecentMeasurements: boolean
): ProbeStatus {
  if (!hasRecentMeasurements || currentValue === undefined) {
    return 'offline';
  }

  // Priority: use firmware status bitfield if available (source of truth from the logger)
  if (probe.last_probe_status != null) {
    const s = probe.last_probe_status;
    const ack = probe.ack_flags ?? 0;

    if (s & FW_ALARM_HIGH || s & FW_ALARM_LOW) return 'alarm';
    if (s & FW_WARNING_HIGH || s & FW_WARNING_LOW) return 'warning';

    // UNCALIBRATED / CERT_EXPIRED → "attention" only if not acked
    const uncalibAcked = !!(ack & ACK_UNCALIBRATED);
    const certAcked    = !!(ack & ACK_CERT_EXPIRED);
    if ((s & FW_UNCALIBRATED) && !uncalibAcked) return 'warning';
    if ((s & FW_CERT_EXPIRED)  && !certAcked)   return 'warning';

    return 'ok';
  }

  // Fallback: recalculate from value vs thresholds (no firmware status available)
  if (probe.alarm_high !== undefined && currentValue > probe.alarm_high) return 'alarm';
  if (probe.alarm_low  !== undefined && currentValue < probe.alarm_low)  return 'alarm';
  if (probe.warning_high !== undefined && currentValue > probe.warning_high) return 'warning';
  if (probe.warning_low  !== undefined && currentValue < probe.warning_low)  return 'warning';

  return 'ok';
}

export function aggregateProbeStatuses(statuses: ProbeStatus[]): ProbeStatus {
  if (statuses.length === 0) return 'unknown';

  // Priorité: alarm > warning > offline > unknown > ok
  if (statuses.includes('alarm')) return 'alarm';
  if (statuses.includes('warning')) return 'warning';
  if (statuses.includes('offline')) return 'offline';
  if (statuses.includes('unknown')) return 'unknown';

  return 'ok';
}
